// Pure helpers for the Template Admin editor: open stored rows for repair, list
// what is still invalid, and gate Save on the strict row schema.
import {
  ChannelSchema,
  RepairableWaterfallRowSchema,
  RepairableWaterfallRowsSchema,
  WaterfallRowsSchema,
  type RepairableWaterfallRow,
  type WaterfallRow,
} from '../wire-schemas';

export interface CorruptionIssue {
  rowName: string;
  rowId: string;
  statusColumn: string;
  field: string;
  value: string;
}

export function isValidChannel(channel: string): boolean {
  return ChannelSchema.safeParse(channel).success;
}

export function invalidChannelCells(rows: readonly RepairableWaterfallRow[]): CorruptionIssue[] {
  return rows.flatMap(r =>
    Object.entries(r.cells).flatMap(([col, c]) =>
      c.channel && !isValidChannel(c.channel)
        ? [{ rowName: r.name || 'Unnamed', rowId: r.id, statusColumn: col, field: 'channel', value: c.channel }]
        : [],
    ),
  );
}

/** Scan unvalidated stored rows (list cards). A row too broken to read at all is skipped. */
export function scanStoredRows(rows: readonly unknown[] | null): CorruptionIssue[] {
  if (!rows) return [];
  return rows.flatMap(raw => {
    const parsed = RepairableWaterfallRowSchema.safeParse(raw);
    return parsed.success ? invalidChannelCells([parsed.data]) : [];
  });
}

/** Rows the editor can open, or null when something other than a channel is wrong. */
export function parseOpenableRows(rows: readonly unknown[]): RepairableWaterfallRow[] | null {
  const parsed = RepairableWaterfallRowsSchema.safeParse(rows);
  return parsed.success ? parsed.data : null;
}

export type SavableRows = { ok: true; rows: WaterfallRow[] } | { ok: false; issues: CorruptionIssue[] };

/** Save is allowed only when every row passes the strict schema the server and the reader use. */
export function parseSavableRows(rows: readonly RepairableWaterfallRow[]): SavableRows {
  const parsed = WaterfallRowsSchema.safeParse(rows);
  if (parsed.success) return { ok: true, rows: parsed.data };
  return { ok: false, issues: invalidChannelCells(rows) };
}
